import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import PageHero from "@/components/site/PageHero";
import { Eyebrow, Reveal, GoldButton } from "@/components/site/motion";
import { IMAGES, companies } from "@/data/content";
import { api } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";

const inputCls =
  "w-full border-b border-ink/20 bg-transparent py-3 text-sm text-ink placeholder:text-smoke focus:border-gold focus:outline-none transition-colors duration-300";

const interestOptions = ["General", ...companies.map((c) => c.name)];

export default function Careers() {
  useSEO(
    "Careers — Hapo Group",
    "Building today's team for the future — open roles across sales, leadership, finance, engineering and technical teams at Hapo Group."
  );
  const [cv, setCv] = useState({ name: "", email: "", interest: "General" });
  const [file, setFile] = useState(null);
  const [state, setState] = useState("idle");
  const [roles, setRoles] = useState(null);

  useEffect(() => {
    api
      .vacancies()
      .then((r) => setRoles(r.data))
      .catch(() => setRoles([]));
  }, []);

  const update = (key) => (e) => setCv((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Please attach your CV (PDF or Word document).");
      return;
    }
    setState("sending");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", cv.name);
    fd.append("email", cv.email);
    fd.append("interest", cv.interest);
    try {
      await api.uploadCv(fd);
      setState("done");
      toast.success("CV received — thank you.");
    } catch (err) {
      setState("idle");
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not upload your CV — please try again.");
    }
  };

  return (
    <>
      <PageHero
        short
        eyebrow="Careers at Hapo Group"
        lines={["Building today's team", "for the future."]}
        description="We're hiring across sales, leadership, finance, engineering and technical teams — find your place in the group."
        image={IMAGES.team}
      />

      <section className="bg-white px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Where you could work">
        <Eyebrow>Nine Companies, One Group</Eyebrow>
        <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
          Where you could make your mark.
        </h2>
        <div className="mt-14">
          {companies.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.04} className="border-t border-ink/10">
              <Link
                to={`/companies/${c.slug}`}
                data-testid={`careers-${c.slug}`}
                className="group grid items-baseline gap-3 py-8 md:grid-cols-12"
              >
                <span className="text-lg font-bold tracking-tight text-ink transition-colors duration-300 group-hover:text-gold md:col-span-4 lg:text-xl">
                  {c.name}
                </span>
                <span className="text-sm leading-relaxed text-smoke md:col-span-7">{c.tabLine}</span>
                <ArrowUpRight className="hidden h-5 w-5 text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100 md:col-span-1 md:block" />
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-mist px-6 py-24 lg:px-12 lg:py-[140px]" aria-label="Open roles">
        <Eyebrow>Open Roles</Eyebrow>
        <h2 className="mt-5 max-w-2xl text-4xl font-extrabold tracking-tighter text-ink sm:text-5xl">
          We're hiring across the group.
        </h2>
        <div className="mt-14" data-testid="vacancies-list">
          {roles === null ? (
            <p className="border-t border-ink/10 py-8 animate-pulse text-[11px] font-semibold uppercase tracking-[0.3em] text-smoke">
              Loading roles…
            </p>
          ) : roles.length === 0 ? (
            <p className="border-t border-ink/10 py-8 text-sm leading-loose text-smoke" data-testid="vacancies-empty">
              No open roles right now — but the group is always growing. Send your CV below and we'll keep it on
              file for what's next.
            </p>
          ) : (
            roles.map((v, i) => (
              <Reveal key={v.id || v.title} delay={i * 0.04} className="border-t border-ink/10">
                <div className="grid items-baseline gap-3 py-8 md:grid-cols-12">
                  <span className="text-lg font-bold tracking-tight text-ink md:col-span-5 lg:text-xl">{v.title}</span>
                  <span className="text-sm leading-relaxed text-smoke md:col-span-4">{v.team}</span>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-smoke md:col-span-2">
                    {v.location}
                  </span>
                  <span className="md:col-span-1 md:text-right">
                    <a
                      href={`mailto:careers@hapogroup.co.za?subject=${encodeURIComponent("Application — " + v.title)}`}
                      data-testid={`apply-${v.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="group inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:text-gold"
                    >
                      Apply
                      <ArrowUpRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </a>
                  </span>
                </div>
              </Reveal>
            ))
          )}
        </div>

        <div className="mt-20 grid gap-12 border-t border-ink/10 pt-16 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Send Your CV</Eyebrow>
            <h3 className="mt-5 text-3xl font-extrabold tracking-tighter text-ink sm:text-4xl">
              Don't see your role? Send it anyway.
            </h3>
            <p className="mt-5 max-w-lg text-sm leading-loose text-smoke md:text-base">
              We're growing across all nine companies. Upload your CV and tell us where you'd fit — we'll keep it
              on file for upcoming roles. PDF or Word, up to 10&nbsp;MB.
            </p>
            <p className="mt-6 text-xs text-smoke">
              Prefer email?{" "}
              <a href="mailto:careers@hapogroup.co.za" className="underline decoration-gold underline-offset-4">
                careers@hapogroup.co.za
              </a>
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            {state === "done" ? (
              <div className="border-l-2 border-gold bg-white p-10" data-testid="cv-success">
                <h4 className="text-xl font-bold tracking-tight text-ink">Thank you, {cv.name.split(" ")[0]}.</h4>
                <p className="mt-3 text-sm leading-loose text-smoke">
                  Your CV is with us. If a role opens up that fits{" "}
                  {cv.interest === "General" ? "your profile" : cv.interest}, we'll be in touch.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} className="border border-ink/10 bg-white p-8 lg:p-10" data-testid="cv-form">
                <div className="grid gap-7 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Name *</span>
                    <input required value={cv.name} onChange={update("name")} data-testid="cv-name-input" className={inputCls} placeholder="Your full name" />
                  </label>
                  <label className="block">
                    <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Email *</span>
                    <input required type="email" value={cv.email} onChange={update("email")} data-testid="cv-email-input" className={inputCls} placeholder="you@example.co.za" />
                  </label>
                </div>
                <label className="mt-7 block">
                  <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-smoke">Area of interest</span>
                  <select value={cv.interest} onChange={update("interest")} data-testid="cv-interest-select" className={`${inputCls} cursor-pointer`}>
                    {interestOptions.map((o) => (
                      <option key={o} value={o}>
                        {o}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="mt-7 flex cursor-pointer items-center justify-center gap-3 border border-dashed border-ink/25 px-6 py-8 text-sm text-smoke transition-colors duration-300 hover:border-gold hover:text-ink">
                  <UploadCloud className="h-5 w-5 text-gold" />
                  {file ? file.name : "Attach your CV — PDF or Word"}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    className="sr-only"
                    data-testid="cv-file-input"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
                <div className="mt-8">
                  <GoldButton type="submit" testId="cv-submit-button">
                    {state === "sending" ? "Uploading..." : "Submit CV"}
                  </GoldButton>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </>
  );
}
